import { Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, Observable, skip } from 'rxjs';

import { UserService } from '@authentication/services/user.service';

import { DIAGNOSTIC_PROFILES_TOKEN } from '../constants/diagnostic-profiles.const';
import { DiagnosticEvent } from '../models/diagnostic-event';
import { DiagnosticProfile } from '../models/diagnostic-profile';
import { DiagnosticDeepDebugConfigService } from './diagnostic-deep-debug-config.service';
import { DiagnosticProfilePreferenceService } from './diagnostic-profile-preference.service';

@Injectable({ providedIn: 'root' })
export class DiagnosticBufferService {
  private readonly buffer: DiagnosticEvent[] = [];
  private readonly profiles: readonly DiagnosticProfile[];
  private activeProfile: DiagnosticProfile;

  /** Emits the active profile whenever it changes. */
  readonly activeProfile$: Observable<DiagnosticProfile>;
  private readonly activeProfileSubject: BehaviorSubject<DiagnosticProfile>;

  constructor(
    @Inject(DIAGNOSTIC_PROFILES_TOKEN)
    profiles: readonly DiagnosticProfile[],
    userService: UserService,
    preference: DiagnosticProfilePreferenceService,
    private readonly deepDebugConfig: DiagnosticDeepDebugConfigService
  ) {
    if (profiles.length === 0) {
      throw new Error('[DiagnosticBufferService] DIAGNOSTIC_PROFILES is empty');
    }
    this.profiles = profiles;
    // Restore the last user-selected profile from localStorage. Falls back
    // to profiles[0] (default) when no preference is stored, or when the
    // stored name no longer matches any registered profile (e.g. policy
    // rename or role downgrade).
    const preferredName = preference.read();
    const restored = preferredName
      ? profiles.find((p) => p.name === preferredName)
      : undefined;
    this.activeProfile = restored ?? profiles[0];

    this.activeProfileSubject = new BehaviorSubject<DiagnosticProfile>(
      this.activeProfile
    );
    this.activeProfile$ = this.activeProfileSubject.asObservable();

    // skip(1): the BehaviorSubject emits its current value synchronously on
    // subscribe; skipping it avoids an immediate clear() at construction time.
    userService.currentUser
      .pipe(skip(1), takeUntilDestroyed())
      .subscribe((user) => {
        if (!user) {
          // Logout: clear the buffer AND reset the active profile so that
          // a different user logging in on the same browser does not
          // inherit user-A's `deepDebug` accumulation (see EAS-2040 review:
          // privacy / data-minimization, plus manifest.profile mislabeling).
          this.clear();
          this.applyProfile(this.profiles[0]);
          preference.clear();
        }
      });
  }

  setActiveProfile(profile: DiagnosticProfile): void {
    if (!this.profiles.some((p) => p.name === profile.name)) {
      throw new Error(
        `[DiagnosticBufferService] unknown profile: ${profile.name}`
      );
    }
    // When switching to a profile that collects no events (i.e. the disabled
    // virtual profile), clear the existing buffer so previously collected data
    // is not retained after the user has chosen to opt out of diagnostics.
    if (profile.eventTypes.length === 0) {
      this.clear();
    }
    this.applyProfile(profile);
  }

  getActiveProfile(): DiagnosticProfile {
    return this.activeProfile;
  }

  push(event: DiagnosticEvent): void {
    if (!this.activeProfile.eventTypes.includes(event.type)) {
      return;
    }

    // Stamp the event with the currently-active profile name. The caller's
    // event.profile is ignored — the service is the source of truth for which
    // profile was active at push time.
    this.buffer.push({ ...event, profile: this.activeProfile.name });

    // Time-based eviction. Assumes events are pushed in roughly chronological
    // order: the loop only scans from the head of the buffer. Out-of-order
    // pushes (e.g. an API event whose timestamp predates already-buffered
    // events) will not be evicted by this pass; that becomes relevant if A3's
    // HTTP interceptor records timestamp at request start but pushes at
    // response time. Strict `<` keeps events whose timestamp equals the
    // cutoff in the buffer.
    const cutoff = Date.now() - this.activeProfile.maxWindowMs;
    while (this.buffer.length > 0 && this.buffer[0].timestamp < cutoff) {
      this.buffer.shift();
    }

    const maxEvents = this.deepDebugConfig.resolveMaxEvents(this.activeProfile);
    while (this.buffer.length > maxEvents) {
      this.buffer.shift();
    }
  }

  snapshot(): readonly DiagnosticEvent[] {
    return [...this.buffer];
  }

  /** Approximate in-memory size of the buffer in bytes (JSON serialization). */
  sizeBytes(): number {
    return JSON.stringify(this.buffer).length;
  }

  clear(): void {
    this.buffer.length = 0;
  }

  /** Single write path for activeProfile — keeps the field and the Subject in sync. */
  private applyProfile(profile: DiagnosticProfile): void {
    this.activeProfile = profile;
    this.activeProfileSubject.next(profile);
  }
}
